import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import Yup from '../../../../utils/yupExtensions';

import FormField from '../../../UI/FormUI/FormField/FormField';
import FormDateField from '../../../UI/FormUI/FormDateField/FormDateField';
import FormSelectField from '../../../UI/FormUI/FormSelectField/FormSelectField';
import Button from '../../../UI/Buttons/Button/Button';

import styles from './WorkExperienceForm.module.css';

function WorkExperienceForm({ onSubmit, ...props }) {
    const { t } = useTranslation();
    const employmentStatuses = [
        { value: 'full_time', label: t('forms.workExperience.employment_status.full_time') },
        { value: 'part_time', label: t('forms.workExperience.employment_status.part_time') },
        { value: 'temporary', label: t('forms.workExperience.employment_status.temporary') },
        { value: 'contract', label: t('forms.workExperience.employment_status.contract') },
        { value: 'internship', label: t('forms.workExperience.employment_status.internship') },
        { value: 'fresher', label: t('forms.workExperience.employment_status.fresher') },
    ];
    const formik = useFormik({
        initialValues: {
            country: '',
            position: '',
            company: '',
            startDate: '',
            endDate: '',
            description: '',
            employmentStatus: '',
            isCurrentlyWorking: false,
        },
        validationSchema: Yup.object({
            country: Yup.string().required(t('forms.workExperience.required')),
            position: Yup.string().required(t('forms.workExperience.required')),
            startDate: Yup.string().required(t('forms.workExperience.required')),
            employmentStatus: Yup.string().required(t('forms.workExperience.required')),
            company: Yup.string(),
            description: Yup.string().max(500, t('forms.workExperience.max_description')),
        }),
        onSubmit: values => {
            const obj = {
                ...values,
                startDate: values.startDate.getFullYear(),
            }
            if (values.endDate && !values.isCurrentlyWorking) {
                obj.endDate = values.endDate.getFullYear()
            }
            
            // If currently working, set isCurrent flag and ensure endDate is null
            if (values.isCurrentlyWorking) {
                obj.isCurrent = true;
                obj.endDate = null;
            }
            
            onSubmit(obj);
        },
    });

    const handleCurrentlyWorkingChange = (e) => {
        const isChecked = e.target.checked;
        formik.setFieldValue('isCurrentlyWorking', isChecked);
        if (isChecked) {
            formik.setFieldValue('endDate', '');
        }
    };

    return (
        <form onSubmit={formik.handleSubmit} {...props}>
            <div>
                <FormField
                    name="position"
                    type="text"
                    placeholder={t('forms.workExperience.working_position')}
                    hasBorder
                    onChange={formik.handleChange}
                    value={formik.values.position}
                    error={formik.touched.position && formik.errors.position}
                />
            </div>
            <div>
                <FormField
                    name="company"
                    type="text"
                    placeholder={t('forms.workExperience.company_name_optional')}
                    hasBorder
                    onChange={formik.handleChange}
                    value={formik.values.company}
                    error={formik.touched.company && formik.errors.company}
                />
            </div>
            <div>
                <FormSelectField
                    name="employmentStatus"
                    type="default"
                    placeholder={t('forms.workExperience.select_employment_status')}
                    hasBorder
                    options={employmentStatuses}
                    onFormikChange={formik.handleChange}
                    value={formik.values.employmentStatus}
                    error={formik.touched.employmentStatus && formik.errors.employmentStatus}
                />
            </div>
            <div>
                <FormField
                    name="description"
                    type="textarea"
                    placeholder={t('forms.workExperience.description')}
                    hasBorder
                    onChange={formik.handleChange}
                    value={formik.values.description}
                    error={formik.touched.description && formik.errors.description}
                />
            </div>
            <div>
                <FormSelectField
                    name="country"
                    type="country"
                    placeholder={t('forms.workExperience.select_country')}
                    hasBorder
                    onFormikChange={formik.handleChange}
                    value={formik.values.country}
                    error={formik.touched.country && formik.errors.country}
                />
            </div>
            <div>
                <FormDateField
                    name="startDate"
                    placeholder={t('forms.workExperience.start_date')}
                    hasBorder
                    showYearPicker
                    dateFormat="yyyy"
                    onChange={formik.handleChange}
                    value={formik.values.startDate}
                    error={formik.touched.startDate && formik.errors.startDate}
                />
            </div>
            <div className={styles.currentlyWorkingCheckbox}>
                <label>
                    <input
                        type="checkbox"
                        name="isCurrentlyWorking"
                        checked={formik.values.isCurrentlyWorking}
                        onChange={handleCurrentlyWorkingChange}
                    />
                    {t('forms.workExperience.currently_working_here', 'I am currently working here')}
                </label>
            </div>
            <div>
                <FormDateField
                    name="endDate"
                    placeholder={t('forms.workExperience.end_date')}
                    hasBorder
                    showYearPicker
                    dateFormat="yyyy"
                    onChange={formik.handleChange}
                    value={formik.values.endDate}
                    error={formik.touched.endDate && formik.errors.endDate}
                    disabled={formik.values.isCurrentlyWorking}
                />
            </div>
            <div>
                <Button type="submit" className={styles.button}>{t('forms.workExperience.add')}</Button>
            </div>
        </form>
    );
}

export default WorkExperienceForm;